import { useState, useMemo, useCallback, useEffect } from 'react';
import { Zap, X, Loader2, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAppSelector } from '@/store/hooks';
import { useMyRosterEntriesQuery } from '@/roster/queries';
import { useActionResult } from '@/hooks/actionResultBus';
import type { ActionResultPayload } from '@/hooks/types';
import { useAvailableActionsQuery } from '../actionQueries';
import type { ActionAttachmentInfo, PlayerAction } from '../actionTypes';

interface ActionAttachmentProps {
  sceneId: string;
  attachment: ActionAttachmentInfo | null;
  onAttach: (action: ActionAttachmentInfo) => void;
  onDetach: () => void;
  targetName?: string;
  /** Correlation id for the send that owns this attachment. */
  actionRequestId?: string | null;
}

export function ActionAttachment({
  sceneId: _sceneId,
  attachment,
  onAttach,
  onDetach,
  targetName,
  actionRequestId = null,
}: ActionAttachmentProps) {
  const [open, setOpen] = useState(false);

  // Resolve active character to numeric ObjectDB pk — same pattern as FocusPanel.
  const activeCharacterName = useAppSelector((state) => state.game.active);
  const { data: myRosterEntries = [] } = useMyRosterEntriesQuery();
  const characterId = useMemo(
    () => myRosterEntries.find((e) => e.name === activeCharacterName)?.character_id ?? null,
    [myRosterEntries, activeCharacterName]
  );

  const { data, isLoading } = useAvailableActionsQuery(characterId, {
    enabled: open,
    staleTime: 30_000,
  });

  // #3760 demo-fidelity review Finding 2 — the attached action's own
  // acknowledged/pending identity (demo Screen 7's "✓ <name> · acknowledged"
  // badge next to the composer's Send button). This still assumes the next
  // `action_result` on the bus is this dispatch's response: unlike
  // `CommandInput.tsx`'s own `handleActionResult` (which now matches on
  // `ActionResultPayload.client_request_id`, #3781), an attached action has
  // no client-minted id of its own to correlate against here, so the gap
  // this component's own attachment identity guards against
  // (`attachmentIdentity`, below) is a real, if narrow, exposure — a
  // different action's success while this one is in flight would flip
  // `acknowledged` early.
  // Resets to `pending` whenever a DIFFERENT action gets attached (a fresh
  // attachment is a fresh, unconfirmed attempt); stays `pending` across
  // re-renders of the SAME attachment until a successful result arrives.
  const attachmentIdentity = attachment
    ? `${attachment.actionKey}:${attachment.target ?? ''}`
    : null;
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    setAcknowledged(false);
  }, [attachmentIdentity]);
  const handleActionResult = useCallback(
    (payload: ActionResultPayload) => {
      if (
        attachmentIdentity &&
        payload.success &&
        (actionRequestId == null || payload.client_request_id === actionRequestId)
      )
        setAcknowledged(true);
    },
    [attachmentIdentity, actionRequestId]
  );
  useActionResult(handleActionResult);

  function handleSelect(action: PlayerAction, isTargeted: boolean) {
    const techniqueId = action.ref.technique_id ?? undefined;
    const actionKey =
      action.ref.registry_key ??
      action.action_template?.name.toLowerCase() ??
      action.display_name.toLowerCase();
    onAttach({
      actionKey,
      name: action.display_name,
      target: isTargeted ? targetName : undefined,
      requiresTarget: isTargeted,
      techniqueId,
    });
    setOpen(false);
  }

  const availableActions: { action: PlayerAction; targeted: boolean }[] = (data?.results ?? [])
    .filter((a) => a.prerequisite_met)
    .map((a) => ({ action: a, targeted: false }));
  const hasActions = availableActions.length > 0;

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Attach action"
            title={attachment ? 'Click to detach action' : 'Attach action'}
            className={`flex h-6 w-6 items-center justify-center rounded text-xs hover:bg-accent hover:text-accent-foreground ${
              attachment ? 'bg-accent text-accent-foreground' : ''
            }`}
            onClick={(e) => {
              if (attachment) {
                e.preventDefault();
                onDetach();
              }
            }}
          >
            <Zap className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="w-56 p-2">
          {isLoading && (
            <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading...
            </div>
          )}
          {data && !hasActions && (
            <p className="p-2 text-sm text-muted-foreground">No actions available</p>
          )}
          {data && hasActions && (
            <div className="space-y-1">
              {availableActions.map(({ action, targeted }) => (
                <button
                  key={`${action.ref.backend}-${action.ref.challenge_instance_id ?? ''}-${action.ref.approach_id ?? ''}-${action.ref.registry_key ?? ''}`}
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  onClick={() => handleSelect(action, targeted)}
                >
                  <Zap className="h-3.5 w-3.5 shrink-0" />
                  <span>{action.display_name}</span>
                  {targeted && (
                    <span className="ml-auto text-xs text-muted-foreground">targeted</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* #3760 demo-fidelity review Finding 1 (round 2): Screen 7 shows ONE
          combined pill (check icon, action name, dot separator, then
          "acknowledged"), not the action chip and the acknowledged/pending
          status as two adjacent elements. The status (icon + text) is
          nested inside this same detach button, after the name and before
          the target text/detach-X, so it reads as one pill while the button
          keeps its exact pre-existing detach behavior (aria-label, onClick,
          structure) tests already cover. */}
      {attachment && (
        <button
          type="button"
          aria-label="Detach action"
          onClick={onDetach}
          className="flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-xs text-accent-foreground hover:bg-accent/80"
        >
          <Zap className="h-3 w-3" />
          {attachment.name}
          <span
            data-testid="action-attachment-status"
            className={`flex items-center gap-1 ${
              acknowledged ? 'text-emerald-600' : 'text-amber-600'
            }`}
          >
            <span aria-hidden="true">&middot;</span>
            {acknowledged ? (
              <Check className="h-3 w-3" aria-hidden="true" />
            ) : (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            )}
            {acknowledged ? 'acknowledged' : 'pending'}
          </span>
          {attachment.requiresTarget && (
            <span className="text-muted-foreground">
              {attachment.target ? `\u2192 ${attachment.target}` : '(select target)'}
            </span>
          )}
          <X className="ml-1 h-3 w-3" />
        </button>
      )}
    </div>
  );
}
